import {
  Alert,
  AppShellContent,
  Button,
  Group,
  IconButton,
  MenuButton,
  Modal,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Title,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ContextualBack } from "../components/navigation/ContextualBack";
import { CompleteWorkOrderModal } from "../components/snowflakes/work-orders/CompleteWorkOrderModal";
import { HistorySection } from "../components/snowflakes/work-orders/HistorySection";
import { ProcedureSection } from "../components/snowflakes/work-orders/ProcedureSection";
import {
  canCancelWorkOrder,
  canDeleteWorkOrder,
  displayWorkOrderNumber,
} from "../components/snowflakes/work-orders/presentation";
import { useWorkOrderAttachmentUpload } from "../components/snowflakes/work-orders/useWorkOrderAttachmentUpload";
import { WorkOrderAttachmentsSection } from "../components/snowflakes/work-orders/WorkOrderAttachmentsSection";
import { DetailCard } from "../components/snowflakes/work-orders/WorkOrderDetailLayout";
import {
  EditDetailsForm,
  type EditDraft,
  editDraftFieldsChanged,
  editDraftTitleError,
  isPlanningReadOnly,
  planningUpdateFields,
  rebaseEditDraft,
  toPlanningEditDraft,
  toScheduleEditDraft,
} from "../components/snowflakes/work-orders/WorkOrderEditForm";
import { WorkOrderOverview } from "../components/snowflakes/work-orders/WorkOrderOverview";
import { WorkOrderSourceMetadata } from "../components/snowflakes/work-orders/WorkOrderSourceMetadata";
import { WorkOrdersBreadcrumb } from "../components/snowflakes/work-orders/WorkOrdersBreadcrumb";
import { WorkOrderTimeAndCostsSection } from "../components/snowflakes/work-orders/WorkOrderTimeEntriesSection";
import { UnsavedChangesDialog } from "../components/UnsavedChangesDialog";
import { useUnsavedChangesGuard } from "../components/useUnsavedChangesGuard";
import { useWorkScope } from "../components/WorkScopeProvider";
import { CancelWorkOrderModal } from "../components/work-orders/CancelWorkOrderModal";
import { DeleteWorkOrderModal } from "../components/work-orders/DeleteWorkOrderModal";
import type { PendingProcedureSave } from "../components/work-orders/ProcedureExecutionLayout";
import { WorkOrderAssigneesField } from "../components/work-orders/WorkOrderAssigneesField";
import {
  type RefreshedWorkOrderDraft,
  WorkOrderDraftRefreshDialog,
} from "../components/work-orders/WorkOrderDraftRefreshDialog";
import { WorkOrderStaleSaveNotice } from "../components/work-orders/WorkOrderStaleSaveNotice";
import { WorkOrderStatusMenu } from "../components/work-orders/WorkOrderStatusMenu";
import { assigneesChanged, assigneeValue } from "../lib/assigneeTokens";
import type { NavigationOrigin } from "../lib/navigationOrigin";
import { stripOriginFromUrl } from "../lib/navigationOriginKeys";
import type { WorkOrderId } from "../lib/workIds";
import {
  buildWorkOrdersReturnSearch,
  type WorkOrdersLocationSearch,
} from "../lib/workOrdersSearch";
import { getWorkScopeSearch } from "../lib/workSearch";
import { useMarkWorkOrderViewedMutation } from "../mutations/useMarkWorkOrderViewedMutation";
import { usePrepareWorkOrderCompletionMutation } from "../mutations/usePrepareWorkOrderCompletionMutation";
import {
  useProcedureAttachmentQuery,
  useUpdateProcedureAttachmentMutation,
} from "../queries/useProcedureAttachment";
import { useProcedureExecutionQuery } from "../queries/useProcedureExecution";
import { useTeamsUsersQuery } from "../queries/useTeamsUsersQuery";
import {
  useWorkOrderAssignmentMutations,
  useWorkOrderAssignmentsQuery,
} from "../queries/useWorkOrderAssignmentMutations";
import type { WorkOrderDetail } from "../queries/useWorkOrderDetailQuery";
import { useWorkOrderDetailQuery } from "../queries/useWorkOrderDetailQuery";
import { useWorkOrderLookupsQuery } from "../queries/useWorkOrderLookupsQuery";
import {
  useCancelWorkOrderMutation,
  useCompleteWorkOrderMutation,
  useDeleteWorkOrderMutation,
  useRestoreWorkOrderMutation,
} from "../queries/useWorkOrderMutations";
import {
  useWorkOrderUpdateMutation,
  WorkOrderUpdateError,
} from "../queries/useWorkOrderUpdateMutation";

function toEditDraft(
  workOrder: WorkOrderDetail,
  assignees: string[],
  procedureId: string | null,
): EditDraft {
  return {
    version: workOrder.version,
    ...toPlanningEditDraft(workOrder),
    description: workOrder.description ?? "",
    instructions: workOrder.instructions ?? "",
    priorityCode: workOrder.priority?.code ?? null,
    statusCode: workOrder.status.code,
    workTypeCode: workOrder.workType.code,
    plannerGroupCode: workOrder.plannerGroup?.code ?? null,
    ...toScheduleEditDraft(workOrder.schedule),
    assignees,
    procedureId,
  };
}

export function WorkOrderDetailScreen({
  origin,
  originSearch,
  workOrderId,
}: {
  /** The record in another module this page was opened from, if any. */
  origin?: NavigationOrigin;
  originSearch?: WorkOrdersLocationSearch;
  workOrderId: WorkOrderId;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const navigate = useNavigate();
  const query = useWorkOrderDetailQuery(workOrderId);
  const workOrder = query.data?.workOrder;
  const { mutate: markWorkOrderViewed } = useMarkWorkOrderViewedMutation();
  // Opening the page is what clears the Work Order's unread item, rather than
  // the detail fetch, which the header also triggers.
  useEffect(() => {
    markWorkOrderViewed(workOrderId);
  }, [markWorkOrderViewed, workOrderId]);
  const [activeTab, setActiveTab] = useState("details");
  const lookupQuery = useWorkOrderLookupsQuery();
  const updateMutation = useWorkOrderUpdateMutation();
  const procedureAttachmentQuery = useProcedureAttachmentQuery(workOrderId);
  const procedureUpdateMutation =
    useUpdateProcedureAttachmentMutation(workOrderId);
  const procedureExecutionQuery = useProcedureExecutionQuery(
    workOrderId,
    Boolean(procedureAttachmentQuery.data),
  );
  const completeMutation = useCompleteWorkOrderMutation();
  const cancelMutation = useCancelWorkOrderMutation();
  const restoreMutation = useRestoreWorkOrderMutation();
  const deleteMutation = useDeleteWorkOrderMutation();
  const assignmentsQuery = useWorkOrderAssignmentsQuery(workOrderId);
  const assignmentMutations = useWorkOrderAssignmentMutations(workOrderId);
  const teamsUsers = useTeamsUsersQuery();
  const assignments = assignmentsQuery.data?.assignments ?? [];
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [editBaseline, setEditBaseline] = useState<EditDraft | null>(null);
  const editSession = useRef(0);
  const [pendingRefresh, setPendingRefresh] =
    useState<RefreshedWorkOrderDraft | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Changing records must invalidate pending refresh results as well as unmounting.
  useEffect(
    () => () => {
      editSession.current += 1;
    },
    [workOrderId],
  );
  const fieldUpdateSaved = useRef(false);
  const procedureDiscardConfirmed = useRef(false);
  const [editStartAttachment, setEditStartAttachment] = useState<{
    attachmentId: number | null;
    procedureId: string | null;
  } | null>(null);
  const [attachmentConflict, setAttachmentConflict] = useState(false);
  const [confirmProcedureDiscard, setConfirmProcedureDiscard] = useState(false);
  const [isCompletionOpen, setIsCompletionOpen] = useState(false);
  const [hasPendingTabTimeChanges, setHasPendingTabTimeChanges] =
    useState(false);
  // Owned here so a pending or failed attachment survives Edit mode, which
  // unmounts the Work Info card that shows it.
  const attachmentUpload = useWorkOrderAttachmentUpload(workOrderId);
  const pendingProcedureSave = useRef<PendingProcedureSave | null>(null);
  const prepareCompletion = usePrepareWorkOrderCompletionMutation(
    workOrderId,
    pendingProcedureSave,
  );
  function requestCompletion() {
    if (prepareCompletion.isPending) return;
    prepareCompletion.mutate(undefined, {
      onSuccess: () => setIsCompletionOpen(true),
    });
  }
  const [linkCopyState, setLinkCopyState] = useState<"default" | "copied">(
    "default",
  );
  const linkCopyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(
    () => () => {
      if (linkCopyResetTimeoutRef.current !== null) {
        clearTimeout(linkCopyResetTimeoutRef.current);
      }
    },
    [],
  );
  async function copyWorkOrderLink() {
    if (!navigator.clipboard?.writeText) return;
    try {
      // A shared link opens with the page's normal back link, not the
      // sender's cross-module one.
      await navigator.clipboard.writeText(
        stripOriginFromUrl(window.location.href),
      );
    } catch {
      return;
    }
    setLinkCopyState("copied");
    if (linkCopyResetTimeoutRef.current !== null) {
      clearTimeout(linkCopyResetTimeoutRef.current);
    }
    linkCopyResetTimeoutRef.current = setTimeout(
      () => setLinkCopyState("default"),
      3000,
    );
  }
  // Server-computed (IWA-19230): read from the latest API response only, so a
  // stale local Role or Team membership can never preserve prior access — see
  // `work-orders/authorization.ts`'s Role→action matrix.
  const canEditFields = workOrder?.actions.EDIT ?? false;
  const canReopen = workOrder?.actions.REOPEN ?? false;
  const canEdit = canEditFields || canReopen;
  const hasUnsavedChanges = Boolean(
    isEditing &&
      editDraft &&
      workOrder &&
      JSON.stringify(editDraft) !==
        JSON.stringify(
          toEditDraft(
            workOrder,
            assignments.map(assigneeValue),
            editStartAttachment?.procedureId ?? null,
          ),
        ),
  );
  const unsavedChangesDialog = useUnsavedChangesGuard({ hasUnsavedChanges });

  // If a background refresh revokes EDIT mid-edit (stale Role/Team access),
  // exit the edit form instead of leaving it visible with no Cancel/Save
  // controls (canEdit gates those, not isEditing).
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally keyed on canEdit only — isEditing/updateMutation are read, not watched, so this doesn't re-fire on every mutation-identity change.
  useEffect(() => {
    if (!canEdit && isEditing) {
      fieldUpdateSaved.current = false;
      editSession.current += 1;
      setPendingRefresh(null);
      setIsEditing(false);
      setEditDraft(null);
      setEditBaseline(null);
      updateMutation.reset();
    }
  }, [canEdit]);
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [isDeletionOpen, setIsDeletionOpen] = useState(false);

  function buildReturnToListSearch(): WorkOrdersLocationSearch {
    const scopeSearch =
      selectedCustomer && selectedFacility
        ? getWorkScopeSearch(selectedCustomer, selectedFacility)
        : {};
    return {
      ...buildWorkOrdersReturnSearch(originSearch, scopeSearch),
      workOrderFullPage: undefined,
      workOrderSelectedId: undefined,
    };
  }

  function startEditing() {
    editSession.current += 1;
    setPendingRefresh(null);
    if (
      !workOrder ||
      assignmentsQuery.isPending ||
      !procedureAttachmentQuery.isSuccess
    )
      return;
    procedureUpdateMutation.reset();
    fieldUpdateSaved.current = false;
    updateMutation.reset();
    const attachment = procedureAttachmentQuery.data;
    setEditStartAttachment({
      attachmentId: attachment?.attachmentId ?? null,
      procedureId: attachment?.procedureId ?? null,
    });
    setAttachmentConflict(false);
    const draft = toEditDraft(
      workOrder,
      assignments.map(assigneeValue),
      procedureAttachmentQuery.data?.procedureId ?? null,
    );
    setEditDraft(draft);
    setEditBaseline(draft);
    setIsEditing(true);
    // The Edit form only renders in the Details tab's panel; jump there so
    // clicking the always-visible header Edit button doesn't silently edit
    // behind whichever other tab is currently showing.
    setActiveTab("details");
  }

  function cancelEditing() {
    fieldUpdateSaved.current = false;
    procedureDiscardConfirmed.current = false;
    setEditStartAttachment(null);
    setAttachmentConflict(false);
    setConfirmProcedureDiscard(false);
    editSession.current += 1;
    setPendingRefresh(null);
    setIsEditing(false);
    setEditDraft(null);
    setEditBaseline(null);
    updateMutation.reset();
    procedureUpdateMutation.reset();
  }

  function setCancellationOpen(open: boolean) {
    cancelMutation.reset();
    setIsCancellationOpen(open);
  }

  function setDeletionOpen(open: boolean) {
    deleteMutation.reset();
    setIsDeletionOpen(open);
  }

  async function confirmDeletion() {
    if (!workOrder) return;
    await deleteMutation.mutateAsync({ workOrderId: workOrder.id });
    setIsDeletionOpen(false);
    void navigate({
      to: "/work-orders",
      search: buildReturnToListSearch(),
    });
  }

  async function saveEditing() {
    if (!editDraft || !workOrder) return;
    const planningReadOnly = isPlanningReadOnly(workOrder.status.code);
    // A blank Title is flagged inline; nothing is sent and every other value
    // stays in the form.
    if (canEditFields && !planningReadOnly && editDraftTitleError(editDraft))
      return;
    procedureUpdateMutation.reset();
    const originalAttachment = editStartAttachment;
    const currentAttachment = procedureAttachmentQuery.data;
    const originalProcedureId = originalAttachment?.procedureId ?? null;
    const procedureChanged = editDraft.procedureId !== originalProcedureId;
    if (
      procedureChanged &&
      originalAttachment &&
      (originalAttachment.attachmentId !==
        (currentAttachment?.attachmentId ?? null) ||
        originalAttachment.procedureId !==
          (currentAttachment?.procedureId ?? null))
    ) {
      setAttachmentConflict(true);
      return;
    }
    const hasProcedureResponses =
      (procedureExecutionQuery.data?.responses.length ?? 0) > 0;
    if (
      procedureChanged &&
      hasProcedureResponses &&
      !procedureDiscardConfirmed.current
    ) {
      setConfirmProcedureDiscard(true);
      return;
    }
    const savedDraft = toEditDraft(
      workOrder,
      editDraft.assignees,
      originalProcedureId,
    );
    const assigneesEdited =
      canEditFields &&
      workOrder.actions.ASSIGN &&
      assigneesChanged(assignments, editDraft.assignees);
    const assignmentOnlyEdit =
      assigneesEdited &&
      !editDraftFieldsChanged(editDraft, savedDraft, canEditFields, false);
    // The field update goes first because it is version-checked; assignment
    // changes follow it, and nothing is changed if the update is refused. An
    // assignment-only edit skips it so the version and history are untouched.
    try {
      if (!fieldUpdateSaved.current && !assignmentOnlyEdit) {
        await updateMutation.mutateAsync(
          canEditFields
            ? {
                workOrderId,
                workOrderNumber: workOrder.number,
                version: editDraft.version,
                ...(planningReadOnly ? {} : planningUpdateFields(editDraft)),
                description: editDraft.description,
                instructions: editDraft.instructions,
                priorityCode: editDraft.priorityCode,
                statusCode: editDraft.statusCode,
                workTypeCode: editDraft.workTypeCode,
                plannerGroupCode: editDraft.plannerGroupCode,
                basicStartDate: editDraft.startDate
                  ? new Date(editDraft.startDate).toISOString()
                  : null,
                dueDate: editDraft.dueDate
                  ? new Date(editDraft.dueDate).toISOString()
                  : null,
              }
            : {
                workOrderId,
                workOrderNumber: workOrder.number,
                version: editDraft.version,
                statusCode: editDraft.statusCode,
              },
        );
        fieldUpdateSaved.current = true;
      }
    } catch {
      return;
    }
    if (procedureChanged) {
      try {
        const updatedAttachment = await procedureUpdateMutation.mutateAsync({
          procedureId: editDraft.procedureId,
          discardResponses: procedureDiscardConfirmed.current,
          expectedAttachmentId: originalAttachment?.attachmentId ?? null,
        });
        setEditStartAttachment({
          attachmentId: updatedAttachment?.attachmentId ?? null,
          procedureId: updatedAttachment?.procedureId ?? null,
        });
        procedureDiscardConfirmed.current = false;
        setAttachmentConflict(false);
      } catch (error) {
        if (
          error instanceof Error &&
          "code" in error &&
          error.code === "CONFLICT"
        ) {
          setAttachmentConflict(true);
        }
        if (
          error instanceof Error &&
          "code" in error &&
          error.code === "RESPONSES_EXIST" &&
          !procedureDiscardConfirmed.current
        ) {
          setConfirmProcedureDiscard(true);
        }
        await refreshDraftVersion();
        return;
      }
    }
    if (assigneesEdited) {
      try {
        await assignmentMutations.replace.mutateAsync({
          current: assignments,
          next: editDraft.assignees,
        });
      } catch {
        // The mutation has already shown its error toast. Reload so a retry
        // diffs against what was actually saved and uses the new version.
        const [detailResult] = await Promise.all([
          query.refetch(),
          assignmentsQuery.refetch(),
        ]);
        const freshWorkOrder = detailResult.data?.workOrder;
        if (freshWorkOrder) {
          setEditDraft((current) =>
            current ? { ...current, version: freshWorkOrder.version } : current,
          );
        }
        return;
      }
    }
    fieldUpdateSaved.current = false;
    procedureDiscardConfirmed.current = false;
    setEditStartAttachment(null);
    setAttachmentConflict(false);
    setConfirmProcedureDiscard(false);
    editSession.current += 1;
    setPendingRefresh(null);
    setIsEditing(false);
    setEditDraft(null);
    setEditBaseline(null);
  }
  async function refreshDraftVersion() {
    const result = await query.refetch();
    if (result.isSuccess && result.data?.workOrder) {
      setEditDraft((current) =>
        current
          ? { ...current, version: result.data.workOrder.version }
          : current,
      );
    }
  }
  function refreshDraft() {
    if (!editBaseline) return;
    const session = ++editSession.current;
    const baseline = editBaseline;
    setPendingRefresh(null);
    void Promise.all([
      query.refetch(),
      procedureAttachmentQuery.refetch(),
      assignmentsQuery.refetch(),
    ]).then(([result, attachmentResult, assignmentResult]) => {
      if (
        session !== editSession.current ||
        !result.isSuccess ||
        !result.data?.workOrder ||
        !attachmentResult.isSuccess ||
        !assignmentResult.isSuccess
      )
        return;
      const attachment = attachmentResult.data;
      setPendingRefresh({
        session,
        baseline,
        latest: toEditDraft(
          result.data.workOrder,
          (assignmentResult.data?.assignments ?? []).map(assigneeValue),
          attachment?.procedureId ?? null,
        ),
        attachment: {
          attachmentId: attachment?.attachmentId ?? null,
          procedureId: attachment?.procedureId ?? null,
        },
      });
    });
  }
  function resolveRefresh(resolution: "mine" | "latest") {
    if (!pendingRefresh || pendingRefresh.session !== editSession.current)
      return;
    fieldUpdateSaved.current = false;
    procedureDiscardConfirmed.current = false;
    setEditStartAttachment(pendingRefresh.attachment);
    setAttachmentConflict(false);
    updateMutation.reset();
    procedureUpdateMutation.reset();
    setEditBaseline(pendingRefresh.latest);
    setEditDraft((current) =>
      current
        ? rebaseEditDraft(
            current,
            pendingRefresh.baseline,
            pendingRefresh.latest,
            resolution,
          )
        : current,
    );
    setPendingRefresh(null);
  }

  function goToTab(value: string | null) {
    if (!value) return;
    setActiveTab(value);
  }

  if (query.isPending && !workOrder) {
    return (
      <AppShellContent pending>
        <span />
      </AppShellContent>
    );
  }

  if (!workOrder) {
    return (
      <AppShellContent>
        <Stack gap={16}>
          <Alert
            title="Unable to load this Work Order."
            tone="negative"
            action={
              <Button
                onClick={() => void query.refetch()}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            }
          />
        </Stack>
      </AppShellContent>
    );
  }

  const canCancelFromStatus = canCancelWorkOrder(workOrder);
  const canDeleteFromStatus = canDeleteWorkOrder(workOrder);
  const hasOverflowActions = canCancelFromStatus || canDeleteFromStatus;

  return (
    <AppShellContent>
      <Stack gap={20}>
        <ContextualBack
          fallback={
            <WorkOrdersBreadcrumb
              canNavigate
              search={buildReturnToListSearch()}
            />
          }
          origin={origin}
        />
        <Group align="end" justify="between" wrap="wrap">
          <Stack gap={8}>
            <Group gap={8}>
              <Title as="h1" size="xl">
                {workOrder.title}
              </Title>
              <IconButton
                aria-label={
                  linkCopyState === "copied"
                    ? "Work Order link copied"
                    : "Copy Work Order link"
                }
                /* @ds-deviation: no "link"/"chain" icon exists in the DS
                   icon registry — "assign-link" is the only chain-link
                   glyph (drawn for the assignment-linking feature, but
                   visually a plain hyperlink icon), reused here as the
                   closest available match. */
                icon={linkCopyState === "copied" ? "check-mark" : "assign-link"}
                onClick={() => void copyWorkOrderLink()}
                size="sm"
                variant="ghost"
              />
            </Group>
            <Group gap={8} wrap="wrap">
              <Text tone="informative">
                {displayWorkOrderNumber(workOrder.number)}
              </Text>
            </Group>
          </Stack>
          <Group aria-label="Work Order actions" gap={8} role="group">
            {workOrder.actions.RESTORE &&
            workOrder.status.code === "CANCELED" ? (
              <Button
                loading={restoreMutation.isPending}
                onClick={() =>
                  restoreMutation.mutate({
                    version: workOrder.version,
                    workOrderId: workOrder.id,
                  })
                }
                size="sm"
              >
                Restore to Open
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                disabled={
                  isEditing ||
                  activeTab === "time-costs" ||
                  activeTab === "history" ||
                  assignmentsQuery.isPending ||
                  !procedureAttachmentQuery.isSuccess
                }
                icon="edit-pen"
                onClick={startEditing}
                size="sm"
                variant="outline"
              >
                Edit
              </Button>
            ) : null}
            {hasOverflowActions ? (
              <MenuButton
                aria-label="More Work Order actions"
                items={[
                  ...(canCancelFromStatus
                    ? [
                        {
                          id: "cancel",
                          label: "Cancel Work Order",
                          leadingIcon: "x-close" as const,
                          onSelect: () => setCancellationOpen(true),
                        },
                      ]
                    : []),
                  ...(canDeleteFromStatus
                    ? [
                        {
                          id: "delete",
                          label: "Delete Work Order",
                          leadingIcon: "trash-delete-bin" as const,
                          onSelect: () => setDeletionOpen(true),
                          tone: "destructive" as const,
                        },
                      ]
                    : []),
                ]}
                size="sm"
                variant="overflow"
              />
            ) : null}
          </Group>
        </Group>

        {query.isRefetchError ? (
          <Alert
            title="Showing this Work Order as of the last successful refresh."
            tone="warning"
            action={
              <Button
                onClick={() => void query.refetch()}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            }
          />
        ) : null}
        {canEdit && procedureAttachmentQuery.isError ? (
          <Alert
            action={
              <Button
                onClick={() => void procedureAttachmentQuery.refetch()}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            }
            title="Unable to load the attached Procedure. Edit is unavailable until it loads."
            tone="negative"
          />
        ) : null}

        {/* Every panel is keepMounted: the Details tab holds in-progress Edit
            and Assignment drafts, and switching tabs must not silently
            discard unsaved input by unmounting/remounting those panels. This
            also avoids refetching each section's query every time its tab is
            revisited. */}
        <Tabs onValueChange={goToTab} value={activeTab}>
          <TabsList aria-label="Work Order detail sections">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="procedure">Procedure</TabsTrigger>
            <TabsTrigger value="time-costs">Time & Costs</TabsTrigger>
            <TabsTrigger
              chipLabel={workOrder.history?.length || undefined}
              value="history"
            >
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent keepMounted value="details">
            <div className="mt-(--spacing-16)">
              <Stack gap={16}>
                {isEditing && editDraft ? (
                  <>
                    <EditDetailsForm
                      workOrder={workOrder}
                      attachments={
                        <WorkOrderAttachmentsSection
                          isCanceled={workOrder.status.code === "CANCELED"}
                          isCompleted={workOrder.status.code === "COMPLETED"}
                          upload={attachmentUpload}
                          workOrderId={workOrder.id}
                        />
                      }
                      descriptionActions={
                        <Group gap={8}>
                          <Button
                            onClick={cancelEditing}
                            size="sm"
                            variant="ghost"
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={
                              !editDraft ||
                              (canEditFields &&
                                !isPlanningReadOnly(workOrder.status.code) &&
                                Boolean(editDraftTitleError(editDraft)))
                            }
                            loading={
                              updateMutation.isPending ||
                              procedureUpdateMutation.isPending ||
                              assignmentMutations.replace.isPending
                            }
                            loadingText="Saving"
                            onClick={() => void saveEditing()}
                            size="sm"
                          >
                            Save
                          </Button>
                        </Group>
                      }
                      allowedStatusCodes={
                        workOrder.actions.TRANSITION
                          ? undefined
                          : {
                              savedCode: workOrder.status.code,
                              targets: workOrder.statusTargets,
                            }
                      }
                      assigneesField={
                        canEditFields && workOrder.actions.ASSIGN ? (
                          <WorkOrderAssigneesField
                            assignments={assignments}
                            canAddSelf={workOrder.actions.CLAIM}
                            currentUserId={
                              teamsUsers.data?.currentUserId ?? null
                            }
                            mode="edit"
                            onChange={(value) =>
                              setEditDraft((current) =>
                                current
                                  ? { ...current, assignees: value }
                                  : current,
                              )
                            }
                            // Only an Administrator may move a Work Order
                            // between statuses freely; everyone else who can
                            // assign is limited to themselves.
                            selfOnly={!workOrder.actions.TRANSITION}
                            value={editDraft.assignees}
                          />
                        ) : undefined
                      }
                      draft={editDraft}
                      lookupData={lookupQuery.data}
                      onChange={(update) => {
                        fieldUpdateSaved.current = false;
                        setEditDraft((current) =>
                          current ? { ...current, ...update } : current,
                        );
                      }}
                      planningReadOnly={isPlanningReadOnly(
                        workOrder.status.code,
                      )}
                      reopenOnly={!canEditFields}
                    />
                    {updateMutation.isError ||
                    procedureUpdateMutation.isError ? (
                      <div className="border-t border-outline-subtle px-(--spacing-20) py-(--spacing-12)">
                        {updateMutation.error instanceof WorkOrderUpdateError &&
                        updateMutation.error.code === "WORK_ORDER_STALE" ? (
                          <WorkOrderStaleSaveNotice onRefresh={refreshDraft} />
                        ) : (
                          <Text size="sm" tone="negative">
                            {procedureUpdateMutation.error instanceof Error
                              ? procedureUpdateMutation.error.message
                              : updateMutation.error instanceof Error
                                ? updateMutation.error.message
                                : "Unable to save Work Order details."}
                          </Text>
                        )}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <WorkOrderOverview
                    assigneesField={
                      <WorkOrderAssigneesField
                        assignments={assignments}
                        mode="view"
                      />
                    }
                    attachments={
                      <WorkOrderAttachmentsSection
                        isCanceled={workOrder.status.code === "CANCELED"}
                        isCompleted={workOrder.status.code === "COMPLETED"}
                        upload={attachmentUpload}
                        workOrderId={workOrder.id}
                      />
                    }
                    facilityTimeZone={selectedFacility?.timezone}
                    originSearch={originSearch}
                    parentOrigin={origin}
                    statusControl={
                      <WorkOrderStatusMenu
                        completionPending={prepareCompletion.isPending}
                        onRequestComplete={requestCompletion}
                        onUpdateError={() => void query.refetch()}
                        workOrder={workOrder}
                      />
                    }
                    workOrder={workOrder}
                  />
                )}
                {workOrder.cancellationReason ? (
                  <DetailCard title="Cancellation">
                    <div className="px-(--spacing-20) py-(--spacing-20)">
                      <Text>{workOrder.cancellationReason}</Text>
                    </div>
                  </DetailCard>
                ) : null}
                {workOrder.status.code === "COMPLETED" &&
                workOrder.completionNote ? (
                  <DetailCard title="Completion notes">
                    <div className="px-(--spacing-20) py-(--spacing-20)">
                      <Text>{workOrder.completionNote}</Text>
                    </div>
                  </DetailCard>
                ) : null}
                <WorkOrderSourceMetadata workOrder={workOrder} />
              </Stack>
            </div>
          </TabsContent>

          <TabsContent keepMounted value="procedure">
            <div className="mt-(--spacing-16)">
              <ProcedureSection
                // Server-computed: covers Administrators and assigned or
                // creating Technicians on an Open or On Hold Work Order.
                canAttach={workOrder.actions.ATTACH_PROCEDURE}
                isEditing={isEditing && workOrder.actions.ATTACH_PROCEDURE}
                instructions={workOrder.instructions}
                onProcedureChange={(procedureId) =>
                  setEditDraft((current) =>
                    current ? { ...current, procedureId } : current,
                  )
                }
                pendingSaveRef={pendingProcedureSave}
                selectedProcedureId={editDraft?.procedureId}
                workOrderId={workOrder.id}
                workOrderStatusCode={workOrder.status.code}
                workOrderNumber={displayWorkOrderNumber(workOrder.number)}
                workOrderTitle={workOrder.title}
              />
            </div>
          </TabsContent>

          <TabsContent keepMounted value="time-costs">
            <div className="mt-(--spacing-16)">
              <WorkOrderTimeAndCostsSection
                canAddTimeToCompleted={
                  workOrder.status.code === "COMPLETED" &&
                  workOrder.actions.REOPEN
                }
                canEditWorkOrder={workOrder.actions.EDIT}
                onPendingChange={setHasPendingTabTimeChanges}
                workOrderId={workOrder.id}
              />
            </div>
          </TabsContent>

          <TabsContent keepMounted value="history">
            <div className="mt-(--spacing-16)">
              <HistorySection history={workOrder.history ?? []} />
            </div>
          </TabsContent>
        </Tabs>
        {attachmentConflict ? (
          <Alert
            action={
              <Button onClick={cancelEditing} size="sm" variant="outline">
                Cancel edit
              </Button>
            }
            title="The attached Procedure changed while you were editing. Cancel and reopen Edit to load the latest Procedure before replacing it."
            tone="negative"
          />
        ) : null}
      </Stack>
      <CompleteWorkOrderModal
        onComplete={async (input) => {
          // Completion validates saved Procedure answers, so save any
          // unsaved ones first.
          await pendingProcedureSave.current?.();
          await completeMutation.mutateAsync({
            ...input,
            workOrderId: workOrder.id,
          });
        }}
        onOpenChange={setIsCompletionOpen}
        open={isCompletionOpen}
        canEditWorkOrder={workOrder.actions.EDIT}
        canAddTimeForCompletionReview={workOrder.actions.COMPLETE}
        hasPendingTimeChanges={hasPendingTabTimeChanges}
        workOrder={{
          completionNote: workOrder.completionNote,
          id: workOrder.id,
          number: workOrder.number,
          title: workOrder.title,
          totalCost: workOrder.costs.total,
        }}
      />
      <CancelWorkOrderModal
        error={
          cancelMutation.error instanceof Error
            ? cancelMutation.error.message
            : null
        }
        isPending={cancelMutation.isPending}
        number={workOrder.number}
        onCancel={(reason) =>
          cancelMutation
            .mutateAsync({
              workOrderId: workOrder.id,
              cancellationReason: reason,
            })
            .then(() => undefined)
        }
        onOpenChange={setCancellationOpen}
        open={isCancellationOpen}
      />
      <DeleteWorkOrderModal
        error={
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : null
        }
        isPending={deleteMutation.isPending}
        number={workOrder.number}
        onDelete={confirmDeletion}
        onOpenChange={setDeletionOpen}
        open={isDeletionOpen}
      />
      <Modal
        open={confirmProcedureDiscard}
        onOpenChange={(open) => {
          if (!open) setConfirmProcedureDiscard(false);
        }}
        title="Discard saved Procedure responses?"
        description="Replacing or removing this Procedure will permanently remove its saved answers and photos."
        footer={
          <>
            <Button
              onClick={() => setConfirmProcedureDiscard(false)}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                procedureDiscardConfirmed.current = true;
                setConfirmProcedureDiscard(false);
                void saveEditing();
              }}
              size="sm"
              intent="destructive"
            >
              Discard responses and save
            </Button>
          </>
        }
      >
        <Text>
          This action cannot be undone. Confirm only if the existing Procedure
          responses are no longer needed.
        </Text>
      </Modal>
      <UnsavedChangesDialog {...unsavedChangesDialog} />
      {isEditing &&
      editDraft &&
      pendingRefresh &&
      pendingRefresh.session === editSession.current ? (
        <WorkOrderDraftRefreshDialog
          draft={editDraft}
          refresh={pendingRefresh}
          onResolve={resolveRefresh}
          onCancel={() => {
            editSession.current += 1;
            setPendingRefresh(null);
          }}
        />
      ) : null}
    </AppShellContent>
  );
}
